import { useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import axios from 'axios';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';
import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import { messages } from './messages';
import style from './transparency-modal.module.less';

export const TransparencyModal = ({ modalFrameworkMethods, itemQuantity = 1, salesOrderItemId, fulfillmentPlanItemId }) => {
  const { closeModal } = modalFrameworkMethods;
  const intl = useIntl();
  const [transparencyCodes, setTransparencyCodes] = useAsyncSafeState('');
  const [validationError, setValidationError] = useAsyncSafeState('');
  const [isValid, setIsValid] = useAsyncSafeState(false);
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const [apiError, setApiError] = useAsyncSafeState('');
  const [saveSuccess, setSaveSuccess] = useAsyncSafeState('');

  const textareaRef = useRef(null);

  const validateCodes = useCallback(
    (codes) => {
      const trimmedCodes = codes.trim();

      if (/,,+/.test(trimmedCodes)) {
        return { isValid: false, errorMessage: 'Please enter valid codes separated by single commas' };
      }

      if (trimmedCodes.startsWith(',')) {
        return { isValid: false, errorMessage: 'Input should not have leading commas' };
      }

      if (trimmedCodes === '') {
        return { isValid: false, errorMessage: '' };
      }

      const noTrailingComma = trimmedCodes.endsWith(',') ? trimmedCodes.slice(0, -1) : trimmedCodes;
      const codeArray = noTrailingComma.split(',');

      if (codeArray.length !== itemQuantity) {
        return { isValid: false, errorMessage: `Please enter exactly ${itemQuantity} code(s)` };
      }

      const invalidCodes = codeArray.filter((code) => !/^[a-zA-Z0-9]+(:[a-zA-Z0-9]+)?$/.test(code.trim()));
      if (invalidCodes.length > 0) {
        return { isValid: false, errorMessage: 'Codes may contain letters, numbers, and colons only' };
      }

      return { isValid: true, errorMessage: '' };
    },
    [itemQuantity],
  );

  const applyValidation = useCallback(
    (codes) => {
      const validationResult = validateCodes(codes);
      setIsValid(validationResult.isValid);
      setValidationError(validationResult.errorMessage);
      return validationResult;
    },
    [validateCodes, setIsValid, setValidationError],
  );

  useEffect(() => {
    applyValidation(transparencyCodes);
  }, [transparencyCodes, applyValidation]);

  useEffect(() => {
    const loadTransparencyCodes = async () => {
      try {
        const response = await axios.get('/transparencycodes/load', {
          params: {
            salesOrderItemGuid: salesOrderItemId,
            orderItemId: fulfillmentPlanItemId,
          },
        });

        if (response.data && response.data.transparencyCodes) {
          setTransparencyCodes(response.data.transparencyCodes);
        }
      } catch {
        // Silently fail - we'll just start with an empty input
      } finally {
        textareaRef?.current?.focus();
      }
    };

    loadTransparencyCodes();
  }, [salesOrderItemId, fulfillmentPlanItemId, itemQuantity, setTransparencyCodes]);

  const handleChange = (e) => {
    setTransparencyCodes(e.target.value);
  };

  // Handle barcode scanner input (scanner sends an Enter key after the scan)
  const handleKeyDown = (e) => {
    setApiError('');
    if (e.key === 'Enter') {
      e.preventDefault();

      // The barcode scanner will have typed the code into the textarea
      // We just need to format it properly with commas
      const trimmedValue = `${e.target.value.trim()},`;
      const codes = trimmedValue.split(',').map((code) => code.trim());
      const joined = codes.join(',');

      setTransparencyCodes(joined);
    }
  };

  const handleSubmit = async () => {
    if (isValid) {
      setIsLoading(true);
      setApiError('');

      const trimmedCodes = transparencyCodes.trim();
      const codeArray = trimmedCodes.split(',').map((code) => code.trim());
      let transparencyCodesString = codeArray.join(',');
      if (transparencyCodesString && transparencyCodesString[transparencyCodesString.length - 1] === ',') {
        // for scanned codes we append a trailing comma, so remove before persisting
        transparencyCodesString = transparencyCodesString.slice(0, -1);
      }

      try {
        await axios.post('/transparencycodes/save', {
          SalesOrderItemGuid: salesOrderItemId,
          OrderItemId: fulfillmentPlanItemId,
          Quantity: itemQuantity,
          TransparencyCodes: transparencyCodesString,
        });

        setSaveSuccess('Transparency code(s) saved successfully.');
        setTimeout(() => {
          setSaveSuccess('');
        }, 5000);
      } catch (error) {
        setApiError(`Failed to save transparency codes: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Modal className={style['transparency-modal']}>
      <ModalHeader>
        <FormattedMessage {...messages.title} />
      </ModalHeader>
      <ModalBody className={style['transparency-modal-body']}>
        <div className={style['transparency-content']}>
          <div className={style['learn-more']}>
            <a
              className={style['learn-more-link']}
              href='https://sell.amazon.com/brand-registry/transparency'
              rel='noopener noreferrer'
              target='_blank'
            >
              <FormattedMessage {...messages.learnMore} />
            </a>
          </div>

          <div className={style['input-container']}>
            <Text textType={TextType.Label} value={<FormattedMessage {...messages.enterCodes} values={{ count: itemQuantity }} />} />
            <Text textType={TextType.Caption} value={<FormattedMessage {...messages.scanCodesInstruction} />} />
            <textarea
              ref={textareaRef}
              className={style['codes-input']}
              disabled={isLoading}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={intl.formatMessage(messages.codesPlaceholder)}
              rows={5}
              value={transparencyCodes}
            />
            {validationError && <Text className={style['validation-error']} textType={TextType.Label} value={validationError} />}
            {apiError && <Text className={style['validation-error']} textType={TextType.Label} value={apiError} />}
            {saveSuccess && <Text className={style['save-success']} textType={TextType.Label} value={saveSuccess} />}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isLoading} onClick={closeModal} theme='secondary'>
          <FormattedMessage {...messages.cancel} />
        </Button>
        <Button disabled={!isValid || isLoading} onClick={handleSubmit} theme='primary'>
          {isLoading ? <LoadingIndicator /> : <FormattedMessage {...messages.submit} />}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

TransparencyModal.propTypes = {
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  itemQuantity: PropTypes.number,
  salesOrderItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fulfillmentPlanItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
